import { useBaseForm } from '@pesto/hooks/src/useBaseForm';
import { Button, Card, CardContent, Typography } from '@pesto/ui';
import { Form } from '@pesto/ui/features/Form/Form';
import { FormInput } from '@pesto/ui/features/Form/FormInput';
import { FormSelect } from '@pesto/ui/features/Form/FormSelect';
import { STATES } from '@pesto/utils/constants/states';
import { Edit, MapPin, Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { EntityAddressCard } from '../../EntityProfile/components/EntityAddressCard';
import { EntityHeader } from '../../EntityProfile/components/EntityHeader';

export interface AddressFormValues {
  addressFirstLine: string;
  addressSecondLine?: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressView {
  addressFirstLine: string | null;
  addressSecondLine?: string | null;
  city: string | null;
  state?: string | null;
  zip: string | null;
  isPoBox?: boolean | null;
}

type AddressFormProps = {
  title: string;
  description?: string;
  address: AddressView | null | undefined;
  emptyText?: string;
} & (
  | { readOnly: true; onSave?: never; saving?: never }
  | {
      readOnly?: false;
      onSave: (values: AddressFormValues) => Promise<{ error?: string } | void>;
      saving?: boolean;
    }
);

const addressSchema = z.object({
  addressFirstLine: z
    .string()
    .max(35, 'Address cannot be more than 35 characters')
    .min(1, 'Address first line is required'),
  addressSecondLine: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z
    .string()
    .min(1, 'State is required')
    .refine((value) => value !== 'IL', {
      message: 'We currently do not operate in this state',
    }),
  zip: z
    .string()
    .min(1, 'Zip code is required')
    .refine((value) => value.length === 5, {
      message: 'Please enter a valid zip code',
    }),
});

const buildDefaults = (address: AddressView | null | undefined): AddressFormValues => ({
  addressFirstLine: address?.addressFirstLine ?? '',
  addressSecondLine: address?.addressSecondLine ?? '',
  city: address?.city ?? '',
  state: address?.state ?? '',
  zip: address?.zip ?? '',
});

const toEntityAddress = (address: AddressView | null | undefined) =>
  address
    ? {
        addressFirstLine: address.addressFirstLine ?? null,
        addressSecondLine: address.addressSecondLine ?? null,
        city: address.city ?? null,
        state: address.state ?? null,
        zip: address.zip ?? null,
        isPoBox: address.isPoBox ?? null,
      }
    : null;

export const AddressForm = (props: AddressFormProps) => {
  const { title, description, address, emptyText, readOnly } = props;
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useBaseForm<AddressFormValues>({
    schema: addressSchema,
    defaultValues: buildDefaults(address),
  });

  const {
    formState: { isDirty, errors },
    handleSubmit: handleSubmitForm,
    watch,
    setValue,
    reset,
  } = form;

  useEffect(() => {
    reset(buildDefaults(address));
  }, [address, reset]);

  const handleEdit = () => {
    setEditMode(true);
    setError(null);
  };

  const handleCancel = () => {
    reset(buildDefaults(address));
    setEditMode(false);
    setError(null);
  };

  const handleSubmit = async (data: AddressFormValues) => {
    if (readOnly) return;

    try {
      const result = await props.onSave(data);

      if (result && 'error' in result && result.error) {
        setError(result.error);

        return;
      }

      setEditMode(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const editButtons = readOnly ? null : editMode ? (
    <>
      <Button
        onClick={(event) => void handleSubmitForm(handleSubmit)(event)}
        disabled={(props.saving ?? false) || !isDirty}
        size="sm"
        className="gap-1"
      >
        <Save className="h-3 w-3" />
        {props.saving ? 'Saving...' : 'Save'}
      </Button>
      <Button variant="outline" onClick={handleCancel} size="sm" className="gap-1">
        <X className="h-3 w-3" />
        Cancel
      </Button>
    </>
  ) : (
    <Button onClick={handleEdit} variant="outline" size="sm" className="gap-1">
      <Edit className="h-3 w-3" />
      Edit
    </Button>
  );

  if (!editMode) {
    return (
      <EntityAddressCard
        config={{
          title,
          description,
          sections: [{ label: title, address: toEntityAddress(address) }],
          emptyText,
          actions: editButtons,
        }}
      />
    );
  }

  return (
    <Card>
      <EntityHeader
        config={{
          avatar: { icon: MapPin },
          title,
          description,
          actions: editButtons,
        }}
      />
      <CardContent>
        <Form onSubmit={(event) => void handleSubmitForm(handleSubmit)(event)}>
          <FormInput
            name="addressFirstLine"
            value={watch('addressFirstLine')}
            onChange={(value: string) => setValue('addressFirstLine', value)}
            label="Address First Line"
            type="text"
            placeholder="Enter street address"
            error={errors.addressFirstLine?.message}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              value={watch('addressSecondLine') ?? ''}
              name="addressSecondLine"
              onChange={(value: string) => setValue('addressSecondLine', value)}
              label="Address Second Line"
              type="text"
              placeholder="Apartment, suite, etc. (optional)"
              error={errors.addressSecondLine?.message}
            />
            <FormInput
              value={watch('city')}
              name="city"
              onChange={(value: string) => setValue('city', value)}
              label="City"
              type="text"
              placeholder="Enter city"
              error={errors.city?.message}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormSelect
              name="state"
              value={watch('state')}
              label="State"
              onChange={(value: string) => setValue('state', value)}
              placeholder="Select state"
              options={STATES.map((state) => ({ value: state.value, label: state.label }))}
              error={errors.state?.message}
            />
            <FormInput
              value={watch('zip')}
              name="zip"
              onChange={(value: string) => setValue('zip', value)}
              label="Zip Code"
              type="text"
              placeholder="Enter zip code"
              error={errors.zip?.message}
              required
            />
          </div>
        </Form>
        {error && (
          <Typography variant="body" className="text-destructive mt-3 text-sm">
            {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
